import { useEffect, useState } from "react";
import TareasKanban from "./TareasKanban";
import { useSelector } from "react-redux";
import { tareasApi } from "src/services/tareasApi";
import { Box, Grid, Paper, Typography } from "@material-ui/core";

const ESTADOS_TAREAS = {
  pendiente: "PENDIENTE",
  enProceso: "EN PROCESO",
  terminada: "TERMINADA",
  canceladoRechazado: "CANCELADO O RECHAZADO",
  facturado: "FACTURADO",
  pendienteFactura: "PENDIENTE DE FACTURACION",
  observada: "OBSERVADA SII",
};

const COLUMNAS_KANBAN = [
  {
    id: "pendiente",
    titulo: "Pendiente",
    color: "#FF9800",
    estados: [ESTADOS_TAREAS.pendiente],
  },
  {
    id: "enProceso",
    titulo: "En proceso",
    color: "#2196F3",
    estados: [ESTADOS_TAREAS.enProceso],
  },
  {
    id: "enEspera",
    titulo: "En espera",
    color: "#9C27B0",
    estados: [ESTADOS_TAREAS.observada, ESTADOS_TAREAS.pendienteFactura],
  },
  {
    id: "terminada",
    titulo: "Terminada",
    color: "#4CAF50",
    estados: [
      ESTADOS_TAREAS.terminada,
      ESTADOS_TAREAS.canceladoRechazado,
      ESTADOS_TAREAS.facturado,
    ],
  },
];

const ordenarTareasPorFecha = (tareas, llave) => {
  return [...tareas].sort((a, b) => {
    if (a[llave] && b[llave]) {
      return new Date(a[llave]) - new Date(b[llave]);
    }
    return 0;
  });
};

export default function TableroKanban({ value }) {
  const { empleado: user, sucursal: sucursalObtenida } = useSelector(
    (state) => state.auth.user,
  );

  const [tareasPorColumna, setTareasPorColumna] = useState({});

  const [getTablero, { data: tableroData, isSuccess: isSuccessTableroData }] =
    tareasApi.useLazyGetTableroQuery();

  useEffect(() => {
    const request = {
      refEmpleado: user.rut,
      sucursal: sucursalObtenida.refSucursal,
    };
    getTablero(request);
  }, [getTablero, sucursalObtenida.refSucursal, user.rut]);

  useEffect(() => {
    if (
      isSuccessTableroData &&
      tableroData?.data &&
      Array.isArray(tableroData.data) &&
      tableroData.data.length > 0
    ) {
      const tareasPorEstado = tableroData.data.reduce((acc, tarea) => {
        const { estado } = tarea;
        if (!acc[estado]) {
          acc[estado] = [];
        }
        acc[estado].push(tarea);
        return acc;
      }, {});

      // Ordenar pendientes por fecha de inicio
      if (tareasPorEstado[ESTADOS_TAREAS.pendiente]) {
        tareasPorEstado[ESTADOS_TAREAS.pendiente] = ordenarTareasPorFecha(
          tareasPorEstado[ESTADOS_TAREAS.pendiente],
          "fechaInicio",
        );
      }

      // Agrupar por columnas
      const columnas = {};
      COLUMNAS_KANBAN.forEach((columna) => {
        let tareas = [];
        columna.estados.forEach((estado) => {
          tareas = [...tareas, ...(tareasPorEstado[estado] || [])];
        });

        // Ordenar terminadas por fecha de término
        if (columna.id === "terminada") {
          tareas = ordenarTareasPorFecha(tareas, "fechaTermino").slice(0, 10);
        }
        // Ordenar en espera por fecha de inicio
        if (columna.id === "enEspera") {
          tareas = ordenarTareasPorFecha(tareas, "fechaInicio");
        }

        columnas[columna.id] = tareas;
      });

      setTareasPorColumna(columnas);
    }
  }, [isSuccessTableroData, tableroData]);

  if (value !== 3) return null;

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {COLUMNAS_KANBAN.map((columna) => (
          <Grid item xs={12} sm={6} md={3} key={columna.id}>
            <Paper
              elevation={0}
              sx={{
                bgcolor: "grey.50",
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: { xs: 300, sm: 400, md: 500 },
                height: "100%",
              }}
            >
              <Box
                sx={{
                  bgcolor: columna.color,
                  color: "white",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {columna.titulo}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.3)",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 600,
                  }}
                >
                  {tareasPorColumna[columna.id]?.length || 0}
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  overflowY: "auto",
                  flexGrow: 1,
                  maxHeight: { xs: 400, md: 600 },
                }}
              >
                <TareasKanban tareas={tareasPorColumna[columna.id] || []} />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
